/**
 * device/apps/ember_arts.ts — The Ember Arts Skill Tree
 *
 * Three trees. Three philosophies. One fire.
 *
 * Flame burns. Root endures. Wind deceives. Players browse the branching
 * trees, spend skill points earned from leveling, and unlock abilities
 * that slot into their skill bar. The tree visualization shows prerequisites
 * as connecting lines, locked/unlocked/maxed states, and tier gating.
 *
 * This is where character build depth lives — the RPG skeleton under
 * the cozy skin.
 */

import type { GameState } from "../../engine/state.js";
import type { EmberTree } from "../../engine/state.js";
import type { DeviceApp, Rect } from "../app.js";
import { registerApp, drawRoundRect, drawText } from "../app.js";
import {
  getTreeAbilities, getTreePassives, getAbility, canUnlockAbility,
  GENERIC_ABILITIES, WEAPON_ABILITIES,
  type AbilityDefinition, type PassiveTalent,
} from "../../combat/skills.js";

// ─── UI State ─────────────────────────────────────────────────────────────

let activeTree: EmberTree = "flame";
let scrollY = 0;
let selectedAbilityId: string | null = null;

// ─── Tree Theme Colors ────────────────────────────────────────────────────

const TREE_COLORS: Record<EmberTree, { primary: string; dim: string; bg: string; glow: string }> = {
  flame: { primary: "#ff6622", dim: "#6b3311", bg: "#1a0a04", glow: "rgba(255, 102, 34, 0.15)" },
  root:  { primary: "#44aa44", dim: "#225522", bg: "#041a08", glow: "rgba(68, 170, 68, 0.15)" },
  wind:  { primary: "#6699dd", dim: "#334466", bg: "#04081a", glow: "rgba(102, 153, 221, 0.15)" },
};

const TREE_NAMES: Record<EmberTree, string> = {
  flame: "Flame — Wrath of the Ember",
  root: "Root — Heartwood's Embrace",
  wind: "Wind — Breath of the Wayward",
};

const TREE_ICONS: Record<EmberTree, string> = {
  flame: "\u{1F525}", // 🔥
  root: "\u{1F333}",  // 🌳
  wind: "\u{1F4A8}",  // 💨
};

// ─── Layout Constants ─────────────────────────────────────────────────────

const TAB_H = 32;
const NODE_W = 56;
const NODE_H = 56;
const NODE_GAP_X = 12;
const NODE_GAP_Y = 16;
const TIER_LABEL_H = 20;
const DETAIL_H = 120;

// ─── App Definition ───────────────────────────────────────────────────────

const emberArtsApp: DeviceApp = {
  id: "ember_arts",
  name: "Ember Arts",
  iconGradient: ["#d4a030", "#6b4f0c"],

  drawIcon(ctx, cx, cy, size) {
    // Three-flame icon
    const s = size * 0.3;
    ctx.fillStyle = "#ff6622";
    drawFlameShape(ctx, cx - s * 0.8, cy + s * 0.2, s * 0.7);
    ctx.fillStyle = "#44aa44";
    drawFlameShape(ctx, cx, cy - s * 0.2, s * 0.9);
    ctx.fillStyle = "#6699dd";
    drawFlameShape(ctx, cx + s * 0.8, cy + s * 0.2, s * 0.7);
  },

  drawContent(ctx, rect, state, _dt) {
    const { x, y, w, h } = rect;
    const colors = TREE_COLORS[activeTree];
    const st = state.player.skillTree;
    const points = st.points;

    // ── Background ─────────────────────────────────────────────────────
    ctx.fillStyle = colors.bg;
    ctx.fillRect(x, y, w, h);

    // ── Tab bar ────────────────────────────────────────────────────────
    const trees: EmberTree[] = ["flame", "root", "wind"];
    const tabW = Math.floor(w / 3);

    for (let i = 0; i < trees.length; i++) {
      const tree = trees[i];
      const tx = x + i * tabW;
      const isActive = tree === activeTree;
      const tc = TREE_COLORS[tree];

      ctx.fillStyle = isActive ? tc.primary : tc.dim;
      ctx.globalAlpha = isActive ? 1 : 0.6;
      drawRoundRect(ctx, tx + 1, y + 1, tabW - 2, TAB_H - 2, 4, ctx.fillStyle);

      ctx.font = "bold 10px monospace";
      ctx.fillStyle = isActive ? "#ffffff" : "#aaaaaa";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`${TREE_ICONS[tree]} ${tree.charAt(0).toUpperCase() + tree.slice(1)}`, tx + tabW / 2, y + TAB_H / 2);
      ctx.globalAlpha = 1;
    }

    // ── Points display ─────────────────────────────────────────────────
    const pointsY = y + TAB_H + 4;
    ctx.font = "bold 9px monospace";
    ctx.fillStyle = points > 0 ? "#d4a030" : "#666666";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(`${points} Skill Point${points !== 1 ? "s" : ""} Available`, x + w / 2, pointsY);

    // ── Tree title ─────────────────────────────────────────────────────
    const titleY = pointsY + 14;
    ctx.font = "8px monospace";
    ctx.fillStyle = colors.primary;
    ctx.fillText(TREE_NAMES[activeTree], x + w / 2, titleY);

    // ── Ability nodes ──────────────────────────────────────────────────
    const abilities = getTreeAbilities(activeTree);
    const passives = getTreePassives(activeTree);
    const unlocked = new Set<string>([
      ...st.flame.map(i => getTreeAbilities("flame")[i]?.id).filter(Boolean),
      ...st.root.map(i => getTreeAbilities("root")[i]?.id).filter(Boolean),
      ...st.wind.map(i => getTreeAbilities("wind")[i]?.id).filter(Boolean),
    ] as string[]);

    const contentY = titleY + 14 - scrollY;
    const maxTier = Math.max(...abilities.map(a => a.tier), ...passives.map(p => p.tier));

    for (let tier = 0; tier <= maxTier; tier++) {
      const tierAbilities = abilities.filter(a => a.tier === tier);
      const tierPassives = passives.filter(p => p.tier === tier);
      const allNodes = [...tierAbilities.map(a => ({ type: "ability" as const, data: a })),
                        ...tierPassives.map(p => ({ type: "passive" as const, data: p }))];

      if (allNodes.length === 0) continue;

      const tierY = contentY + tier * (NODE_H + NODE_GAP_Y + TIER_LABEL_H);

      // Tier label
      const tierLabel = tier === 0 ? "Starter" : tier === 3 ? "Ultimate" : `Tier ${tier}`;
      ctx.font = "bold 8px monospace";
      ctx.fillStyle = colors.dim;
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText(tierLabel, x + 6, tierY);

      // Draw nodes
      const totalW = allNodes.length * NODE_W + (allNodes.length - 1) * NODE_GAP_X;
      const startX = x + (w - totalW) / 2;

      for (let ni = 0; ni < allNodes.length; ni++) {
        const node = allNodes[ni];
        const nx = startX + ni * (NODE_W + NODE_GAP_X);
        const ny = tierY + TIER_LABEL_H;
        const nodeId = node.data.id;
        const isUnlocked = unlocked.has(nodeId);
        const canUnlock = !isUnlocked && points > 0 && canUnlockAbility(nodeId, state.player.combat.level, unlocked);
        const isSelected = selectedAbilityId === nodeId;

        // Node background
        if (isSelected) {
          ctx.fillStyle = colors.glow;
          drawRoundRect(ctx, nx - 3, ny - 3, NODE_W + 6, NODE_H + 6, 8, ctx.fillStyle);
        }

        ctx.fillStyle = isUnlocked ? colors.primary : canUnlock ? colors.dim : "#1a1a1a";
        drawRoundRect(ctx, nx, ny, NODE_W, NODE_H, 6, ctx.fillStyle);

        // Border
        ctx.strokeStyle = isUnlocked ? colors.primary : canUnlock ? colors.primary : "#333333";
        ctx.lineWidth = isSelected ? 2 : 1;
        drawRoundRect(ctx, nx, ny, NODE_W, NODE_H, 6, undefined, ctx.strokeStyle);

        // Icon
        const icon = node.type === "ability" ? (node.data as AbilityDefinition).icon : (node.data as PassiveTalent).icon;
        ctx.font = `${NODE_W * 0.45}px serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.globalAlpha = isUnlocked ? 1 : canUnlock ? 0.7 : 0.3;
        ctx.fillStyle = "#ffffff";
        ctx.fillText(icon, nx + NODE_W / 2, ny + NODE_H / 2 - 4);
        ctx.globalAlpha = 1;

        // Name
        ctx.font = "7px monospace";
        ctx.fillStyle = isUnlocked ? "#ffffff" : "#888888";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        const name = node.data.name.length > 8 ? node.data.name.slice(0, 7) + "." : node.data.name;
        ctx.fillText(name, nx + NODE_W / 2, ny + NODE_H - 2);

        // "Can unlock" indicator
        if (canUnlock) {
          ctx.fillStyle = "#d4a030";
          ctx.beginPath();
          ctx.arc(nx + NODE_W - 4, ny + 4, 3, 0, Math.PI * 2);
          ctx.fill();
        }

        // Type badge
        if (node.type === "passive") {
          ctx.font = "bold 6px monospace";
          ctx.fillStyle = "#aaaaaa";
          ctx.textAlign = "right";
          ctx.textBaseline = "top";
          ctx.fillText("P", nx + NODE_W - 3, ny + 2);
        }
      }

      // Draw prerequisite lines
      for (const node of allNodes) {
        const prereqs = node.data.prerequisites;
        const ni = allNodes.indexOf(node);
        const nx = startX + ni * (NODE_W + NODE_GAP_X) + NODE_W / 2;
        const ny = tierY + TIER_LABEL_H;

        for (const preReqId of prereqs) {
          // Find the prerequisite node in the previous tier
          const prevTier = tier - 1;
          const prevAbilities = abilities.filter(a => a.tier === prevTier);
          const prevPassives = passives.filter(p => p.tier === prevTier);
          const prevAll = [...prevAbilities, ...prevPassives];
          const preIdx = prevAll.findIndex(n => n.id === preReqId);
          if (preIdx < 0) continue;

          const prevTotalW = prevAll.length * NODE_W + (prevAll.length - 1) * NODE_GAP_X;
          const prevStartX = x + (w - prevTotalW) / 2;
          const px = prevStartX + preIdx * (NODE_W + NODE_GAP_X) + NODE_W / 2;
          const py = contentY + prevTier * (NODE_H + NODE_GAP_Y + TIER_LABEL_H) + TIER_LABEL_H + NODE_H;

          ctx.strokeStyle = unlocked.has(preReqId) ? colors.primary : "#333333";
          ctx.lineWidth = 1;
          ctx.globalAlpha = 0.5;
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(nx, ny);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
      }
    }

    // ── Detail panel for selected ability ──────────────────────────────
    if (selectedAbilityId) {
      const aDef = getAbility(selectedAbilityId);
      if (aDef) {
        drawAbilityDetail(ctx, x, y + h - DETAIL_H, w, DETAIL_H, aDef, state, unlocked, colors);
      }
    }
  },

  handleTap(localX, localY, state) {
    const trees: EmberTree[] = ["flame", "root", "wind"];
    const w = 200; // approximate content width

    // Tab bar
    if (localY < TAB_H) {
      const tabW = Math.floor(w / trees.length);
      const idx = Math.floor(localX / tabW);
      if (idx >= 0 && idx < trees.length) {
        activeTree = trees[idx];
        selectedAbilityId = null;
        scrollY = 0;
        state.ui.uiSounds.push("ui_tab");
        return true;
      }
    }

    // Node taps — find which node was tapped
    const abilities = getTreeAbilities(activeTree);
    const passives = getTreePassives(activeTree);
    const maxTier = Math.max(...abilities.map(a => a.tier), ...passives.map(p => p.tier));
    const contentY = TAB_H + 32 - scrollY;

    for (let tier = 0; tier <= maxTier; tier++) {
      const tierAbilities = abilities.filter(a => a.tier === tier);
      const tierPassives = passives.filter(p => p.tier === tier);
      const allNodes = [...tierAbilities, ...tierPassives];
      if (allNodes.length === 0) continue;

      const tierY = contentY + tier * (NODE_H + NODE_GAP_Y + TIER_LABEL_H) + TIER_LABEL_H;
      const totalNodeW = allNodes.length * NODE_W + (allNodes.length - 1) * NODE_GAP_X;
      const startX = (w - totalNodeW) / 2;

      for (let ni = 0; ni < allNodes.length; ni++) {
        const nx = startX + ni * (NODE_W + NODE_GAP_X);
        const ny = tierY;
        if (localX >= nx && localX < nx + NODE_W && localY >= ny && localY < ny + NODE_H) {
          const node = allNodes[ni];
          if (selectedAbilityId === node.id) {
            // Double-tap: try to unlock
            tryUnlock(state, node.id);
          } else {
            selectedAbilityId = node.id;
          }
          state.ui.uiSounds.push("ui_click");
          return true;
        }
      }
    }

    selectedAbilityId = null;
    return false;
  },

  handleDrag(dy) {
    scrollY = Math.max(0, scrollY - dy);
  },

  onOpen() {
    scrollY = 0;
    selectedAbilityId = null;
  },

  badge(state) {
    return state.player.skillTree.points;
  },
};

// ─── Unlock Logic ─────────────────────────────────────────────────────────

function tryUnlock(state: GameState, abilityId: string): void {
  const st = state.player.skillTree;
  if (st.points <= 0) return;

  const unlocked = new Set<string>([
    ...st.flame.map(i => getTreeAbilities("flame")[i]?.id).filter(Boolean),
    ...st.root.map(i => getTreeAbilities("root")[i]?.id).filter(Boolean),
    ...st.wind.map(i => getTreeAbilities("wind")[i]?.id).filter(Boolean),
  ] as string[]);

  if (unlocked.has(abilityId)) return;
  if (!canUnlockAbility(abilityId, state.player.combat.level, unlocked)) return;

  // Find which tree and index
  const trees: EmberTree[] = ["flame", "root", "wind"];
  for (const tree of trees) {
    const abilities = getTreeAbilities(tree);
    const idx = abilities.findIndex(a => a.id === abilityId);
    if (idx >= 0) {
      st[tree].push(idx);
      st.points--;
      state.ui.uiSounds.push("ui_equip");

      // Auto-slot into first empty skill bar slot
      const bar = state.player.abilityBar;
      const emptySlot = bar.slots.findIndex(s => s === null);
      if (emptySlot >= 0) {
        bar.slots[emptySlot] = { id: abilityId, cooldownEnd: 0, charges: abilities[idx].maxCharges };
      }
      return;
    }

    const passives = getTreePassives(tree);
    const pIdx = passives.findIndex(p => p.id === abilityId);
    if (pIdx >= 0) {
      // For passives, store the index in the tree's list
      // (passives use same unlock array — offset by abilities length)
      const offset = getTreeAbilities(tree).length;
      st[tree].push(offset + pIdx);
      st.points--;
      state.ui.uiSounds.push("ui_equip");
      return;
    }
  }
}

// ─── Ability Detail Panel ─────────────────────────────────────────────────

function drawAbilityDetail(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  def: AbilityDefinition,
  state: GameState,
  _unlocked: Set<string>,
  colors: { primary: string; dim: string; bg: string },
): void {
  // Dark panel
  ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
  ctx.fillRect(x, y, w, h);

  // Top border accent
  ctx.fillStyle = colors.primary;
  ctx.fillRect(x, y, w, 2);

  // Icon + Name
  const pad = 8;
  ctx.font = `${18}px serif`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(def.icon, x + pad, y + pad);

  ctx.font = "bold 11px monospace";
  ctx.fillStyle = colors.primary;
  ctx.fillText(def.name, x + pad + 26, y + pad);

  // Cost + cooldown
  ctx.font = "9px monospace";
  ctx.fillStyle = "#d4a030";
  ctx.fillText(
    `${def.cost} EP · ${def.cooldown > 0 ? def.cooldown + "s CD" : "No CD"}${def.maxCharges > 1 ? ` · ${def.maxCharges} charges` : ""}`,
    x + pad + 26, y + pad + 14,
  );

  // Description
  ctx.font = "8px monospace";
  ctx.fillStyle = "#cccccc";
  const descLines = wrapText(def.description, 32);
  for (let i = 0; i < descLines.length; i++) {
    ctx.fillText(descLines[i], x + pad, y + pad + 32 + i * 11);
  }

  // Lore
  ctx.font = "italic 7px monospace";
  ctx.fillStyle = "#666666";
  ctx.fillText(`"${def.lore}"`, x + pad, y + h - pad - 8);

  // Level requirement
  ctx.font = "7px monospace";
  const meetsLevel = state.player.combat.level >= def.levelRequired;
  ctx.fillStyle = meetsLevel ? "#888888" : "#cc4444";
  ctx.textAlign = "right";
  ctx.fillText(`Requires Level ${def.levelRequired}`, x + w - pad, y + pad);
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function wrapText(text: string, maxChars: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (current.length + word.length + 1 > maxChars && current.length > 0) {
      lines.push(current);
      current = word;
    } else {
      current = current.length > 0 ? current + " " + word : word;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function drawFlameShape(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number): void {
  ctx.beginPath();
  ctx.moveTo(cx, cy - size);
  ctx.bezierCurveTo(cx - size * 0.6, cy - size * 0.3, cx - size * 0.4, cy + size * 0.3, cx, cy + size * 0.5);
  ctx.bezierCurveTo(cx + size * 0.4, cy + size * 0.3, cx + size * 0.6, cy - size * 0.3, cx, cy - size);
  ctx.closePath();
  ctx.fill();
}

// ─── Register ─────────────────────────────────────────────────────────────

registerApp(emberArtsApp);

export { emberArtsApp };
